// 音声サーバ（VS）接続先の解決（AI-WF265 / 要望#213・設計v2 §2.7）。
// - 既定 '/voice'＝同一オリジン path（vite dev proxy が VS へ転送。VS は CORS 未設定のため）。
// - 本番等で VS へ直結する場合は VITE_VOICE_BASE_URL に http(s) の絶対 origin を設定
//   （その場合 VS 側の CORS 許可が必要＝申し送り）。

import { useAuthStore } from '../auth/auth-store';

const raw = (import.meta.env.VITE_VOICE_BASE_URL ?? '').trim();
const base = (raw !== '' ? raw : '/voice').replace(/\/+$/, '');

/** HTTP 用 URL（fetch は相対 path でも動く） */
export function voiceHttpUrl(path: string): string {
  return `${base}${path}`;
}

/**
 * VS 向けの認証ヘッダ。VS が VOICE_AUTH_REQUIRED=1 のとき必須。
 * konecco 本体と同じ JWT をそのまま使う（VS 側が BE の JWT_SECRET で検証する）。
 * VS が無認証運用（tailnet 内）なら送っても無視されるので、常に付けてよい。
 */
export function voiceAuthHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * WS 用 URL（http(s)→ws(s) 置換。相対 base は location.origin から組み立て）。
 * ブラウザの WebSocket は Authorization ヘッダを付けられないため JWT を ?token= で渡す
 * （BE 側 chat-stream の EventSource と同じ方式）。
 */
export function voiceWsUrl(path: string): string {
  const abs = /^https?:/i.test(base) ? base : `${window.location.origin}${base}`;
  const url = `${abs.replace(/^http/i, 'ws')}${path}`;
  const token = useAuthStore.getState().accessToken;
  if (!token) return url;
  return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
}

/** VS が返す絶対 signalUrl から path だけを取り出して proxy/base 経由の WS URL にする */
export function signalWsUrlFrom(signalUrl: string): string {
  try {
    return voiceWsUrl(new URL(signalUrl).pathname);
  } catch {
    return voiceWsUrl(signalUrl.startsWith('/') ? signalUrl : `/${signalUrl}`);
  }
}

// INTERIM_TURN_TAKING: STT final → konecco 転送（FE 暫定ターンテイキング）の既定値。
// #212（VS 側 onFinal→sendUtterance）が 2026-07-11 dev マージ済のため、**既定 OFF**（未設定なら送らない）。
// VS が自動転送するので FE も送ると二重投稿になる。旧 VS（自動転送なし）で検証したい時だけ
// VITE_VOICE_INTERIM_FORWARD=1 で ON にする。二重送信回避プロトコル（設計v2 §v2-2）。
export const INTERIM_FORWARD_DEFAULT = import.meta.env.VITE_VOICE_INTERIM_FORWARD === '1';
